import {
  type HTMLAttributes,
  type MouseEvent as ReactMouseEvent,
  type PointerEvent as ReactPointerEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { kaiFontFamily } from '../../../../core/tokens';
import {
  formatReviewFlowPercent,
  getReviewFlowScrubberMarks,
  resolveReviewFlowScrubberActiveIndex,
} from '../reviewFlowScrubberLayout';
import { REVIEW_COLORS, REVIEW_RADII, REVIEW_SPACING } from '../reviewFlowTokens';
import type { ReviewFlowScrubberModel } from '../reviewFlowTypes';
import {
  resolveStyleOverride,
  type StyleOverride,
} from '../styleOverrides';

export interface ReviewFlowScrubberProps extends Omit<HTMLAttributes<HTMLDivElement>, 'onSelect'> {
  model: ReviewFlowScrubberModel;
  onSelectItem?: (id: string) => void;
  density?: 'default' | 'compact';
  styleOverride?: StyleOverride;
}

type ScrubberDragMark = {
  id: string;
  index: number;
  x: number;
};

type ScrubberDragState = {
  pointerId: number;
  originX: number;
  minOffsetPx: number;
  maxOffsetPx: number;
  previousClientX: number;
  lastIndex: number;
  marks: ScrubberDragMark[];
};

const SCRUBBER_SELECTION_THRESHOLD_RATIO = 0.55;
const SCRUBBER_SNAP_HIGHLIGHT_MS = 480;
const SCRUBBER_HIGHLIGHT_BACKGROUND = 'rgba(245, 183, 0, 0.16)';
const SCRUBBER_HIGHLIGHT_BORDER = 'rgba(245, 183, 0, 0.85)';
const SCRUBBER_HIGHLIGHT_SHADOW = '0 0 0 4px rgba(245, 183, 0, 0.22), 0 0 0 1px rgba(245, 183, 0, 0.56)';
const SCRUBBER_TRACK_TRANSITION = 'width 180ms cubic-bezier(0.16, 1, 0.3, 1)';
const SCRUBBER_MOTION_TRANSITION = [
  'left 180ms cubic-bezier(0.16, 1, 0.3, 1)',
  'transform 220ms cubic-bezier(0.16, 1, 0.3, 1)',
  'width 160ms ease',
  'height 160ms ease',
  'opacity 160ms ease',
  'background-color 160ms ease',
  'border-color 160ms ease',
  'box-shadow 180ms ease',
].join(', ');

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);

const getScrubberDragClientX = (clientX: number, dragState: Pick<ScrubberDragState, 'originX' | 'minOffsetPx' | 'maxOffsetPx'>) =>
  clamp(
    clientX,
    dragState.originX + dragState.minOffsetPx,
    dragState.originX + dragState.maxOffsetPx,
  );

const getScrubberDragOffsetPx = (clientX: number, dragState: Pick<ScrubberDragState, 'originX' | 'minOffsetPx' | 'maxOffsetPx'>) =>
  Math.round(
    getScrubberDragClientX(clientX, dragState) - dragState.originX,
  );

const formatScrubberPointTransform = (dragOffsetPx: number) =>
  dragOffsetPx === 0
    ? 'translate(-50%, -50%)'
    : `translate(calc(-50% + ${dragOffsetPx}px), -50%)`;

const getScrubberDragMarks = (
  marks: ReturnType<typeof getReviewFlowScrubberMarks>,
  activeIndex: number,
  rect: Pick<DOMRect, 'left' | 'width'>,
): ScrubberDragMark[] => {
  const positionedMarks = marks
    .flatMap(mark =>
      mark.item && !mark.item.disabled
        ? [{
          id: mark.item.id,
          index: activeIndex + mark.distance,
          x: rect.left + rect.width * (mark.percent / 100),
        }]
        : []
    )
    .sort((a, b) => a.x - b.x);

  return positionedMarks.map((mark, index) => {
    if (mark.index === activeIndex) return mark;
    const towardActive = positionedMarks[mark.index > activeIndex ? index - 1 : index + 1];
    if (!towardActive) return mark;
    return {
      ...mark,
      x: towardActive.x + (mark.x - towardActive.x) * SCRUBBER_SELECTION_THRESHOLD_RATIO,
    };
  });
};

const getCrossedScrubberDragMark = (
  marks: readonly ScrubberDragMark[],
  lastIndex: number,
  previousClientX: number,
  clientX: number,
) => {
  if (clientX > previousClientX) {
    const crossedMarks = marks.filter(mark => mark.index > lastIndex && mark.x > previousClientX && mark.x <= clientX);
    return crossedMarks[crossedMarks.length - 1];
  }

  if (clientX < previousClientX) {
    return marks.filter(mark => mark.index < lastIndex && mark.x < previousClientX && mark.x >= clientX)[0];
  }

  return undefined;
};

export const ReviewFlowScrubber = ({
  model,
  onSelectItem,
  density = 'default',
  styleOverride: scrubberCss,
  style,
  onClick,
  onPointerCancel,
  onPointerDown,
  onPointerMove,
  onPointerUp,
  ...props
}: ReviewFlowScrubberProps) => {
  const compact = density === 'compact';
  const activeIndex = resolveReviewFlowScrubberActiveIndex(model);
  const marks = getReviewFlowScrubberMarks(model, activeIndex);
  const activeMark = marks.find(mark => mark.item && mark.distance === 0);
  const activePercent = activeMark?.percent ?? 0;
  const [dragging, setDragging] = useState(false);
  const [dragOffsetPx, setDragOffsetPx] = useState(0);
  const [hoveredItemId, setHoveredItemId] = useState<string | null>(null);
  const [snapHighlightItemId, setSnapHighlightItemId] = useState<string | null>(null);
  const dragStateRef = useRef<ScrubberDragState | null>(null);
  const snapHighlightTimerRef = useRef<number | null>(null);
  const suppressNextClickRef = useRef(false);

  const clearSnapHighlightTimer = useCallback(() => {
    if (snapHighlightTimerRef.current == null) return;
    window.clearTimeout(snapHighlightTimerRef.current);
    snapHighlightTimerRef.current = null;
  }, []);

  const showSnapHighlight = useCallback((itemId: string) => {
    clearSnapHighlightTimer();
    setSnapHighlightItemId(itemId);
    snapHighlightTimerRef.current = window.setTimeout(() => {
      snapHighlightTimerRef.current = null;
      setSnapHighlightItemId(currentItemId => currentItemId === itemId ? null : currentItemId);
    }, SCRUBBER_SNAP_HIGHLIGHT_MS);
  }, [clearSnapHighlightTimer]);

  const clearDragState = useCallback(() => {
    dragStateRef.current = null;
  }, []);

  const stopDrag = useCallback(() => {
    clearDragState();
    setDragging(false);
    setDragOffsetPx(0);
  }, [clearDragState]);

  const selectItem = useCallback((itemId: string) => {
    showSnapHighlight(itemId);
    onSelectItem?.(itemId);
  }, [onSelectItem, showSnapHighlight]);

  const selectDragItem = useCallback((dragState: ScrubberDragState, itemId: string, index: number) => {
    if (index === dragState.lastIndex) return;
    dragState.lastIndex = index;
    selectItem(itemId);
  }, [selectItem]);

  const handleScrubberClick = useCallback((event: ReactMouseEvent<HTMLDivElement>) => {
    onClick?.(event);
    if (event.defaultPrevented || model.disabled || !onSelectItem) return;

    if (suppressNextClickRef.current) {
      suppressNextClickRef.current = false;
      return;
    }

    const target = event.target instanceof Element ? event.target : null;
    if (target?.closest('[data-review-flow-scrubber-item="true"]')) return;

    const rect = event.currentTarget.getBoundingClientRect();
    if (rect.width <= 0) return;

    const originX = rect.left + rect.width * (activePercent / 100);
    const clickX = clamp(event.clientX, rect.left, rect.right);
    const nextMark = getCrossedScrubberDragMark(
      getScrubberDragMarks(marks, activeIndex, rect),
      activeIndex,
      originX,
      clickX,
    );

    if (nextMark) selectItem(nextMark.id);
  }, [activeIndex, activePercent, marks, model.disabled, onClick, onSelectItem, selectItem]);

  const handleScrubberPointerDown = useCallback((event: ReactPointerEvent<HTMLDivElement>) => {
    if (event.button !== 0 || model.disabled || !onSelectItem) return;

    const target = event.target instanceof Element ? event.target : null;
    const dragHandle = target?.closest('[data-review-flow-scrubber-thumb="true"], [data-review-flow-scrubber-item="true"][data-review-flow-active="true"]');
    if (!dragHandle) return;

    const rect = event.currentTarget.getBoundingClientRect();
    if (rect.width <= 0) return;

    suppressNextClickRef.current = false;

    const originX = rect.left + rect.width * (activePercent / 100);
    dragStateRef.current = {
      pointerId: event.pointerId,
      originX,
      minOffsetPx: rect.left - originX,
      maxOffsetPx: rect.right - originX,
      previousClientX: originX,
      lastIndex: activeIndex,
      marks: getScrubberDragMarks(marks, activeIndex, rect),
    };
    setDragging(true);
    setDragOffsetPx(0);
    event.currentTarget.setPointerCapture?.(event.pointerId);
  }, [activeIndex, activePercent, marks, model.disabled, onSelectItem]);

  const handleScrubberPointerMove = useCallback((event: ReactPointerEvent<HTMLDivElement>) => {
    const dragState = dragStateRef.current;
    if (!dragState || dragState.pointerId !== event.pointerId) return;

    suppressNextClickRef.current = true;

    const dragClientX = getScrubberDragClientX(event.clientX, dragState);
    setDragOffsetPx(getScrubberDragOffsetPx(event.clientX, dragState));

    const nextMark = getCrossedScrubberDragMark(dragState.marks, dragState.lastIndex, dragState.previousClientX, dragClientX);
    if (nextMark) selectDragItem(dragState, nextMark.id, nextMark.index);
    dragState.previousClientX = dragClientX;

    event.preventDefault();
  }, [selectDragItem]);

  const handleScrubberPointerEnd = useCallback((event: ReactPointerEvent<HTMLDivElement>) => {
    const dragState = dragStateRef.current;
    if (!dragState || dragState.pointerId !== event.pointerId) return;

    event.currentTarget.releasePointerCapture?.(event.pointerId);
    stopDrag();
  }, [stopDrag]);

  useEffect(() => clearDragState, [clearDragState]);
  useEffect(() => clearSnapHighlightTimer, [clearSnapHighlightTimer]);

  useEffect(() => {
    if (model.disabled) stopDrag();
  }, [model.disabled, stopDrag]);

  const scrubberCursor = model.disabled ? 'not-allowed' : 'default';
  const draggableCursor = model.disabled ? 'not-allowed' : onSelectItem ? (dragging ? 'grabbing' : 'grab') : 'default';
  const activePointTransform = formatScrubberPointTransform(dragOffsetPx);
  const thumbHighlighted = snapHighlightItemId === activeMark?.item?.id;

  return (
    <div
      data-review-flow-scrubber='true'
      data-review-flow-scrubber-layout={model.layout ?? 'linear'}
      data-review-flow-density={density}
      onClick={handleScrubberClick}
      onPointerCancel={event => {
        onPointerCancel?.(event);
        if (!event.defaultPrevented) handleScrubberPointerEnd(event);
      }}
      onPointerDown={event => {
        onPointerDown?.(event);
        if (!event.defaultPrevented) handleScrubberPointerDown(event);
      }}
      onPointerMove={event => {
        onPointerMove?.(event);
        if (!event.defaultPrevented) handleScrubberPointerMove(event);
      }}
      onPointerUp={event => {
        onPointerUp?.(event);
        if (!event.defaultPrevented) handleScrubberPointerEnd(event);
      }}
      style={{
        touchAction: 'none',
        cursor: scrubberCursor,
        position: 'relative',
        minHeight: compact ? 16 : 32,
        padding: compact ? '6px 0' : `${REVIEW_SPACING.medium}px 0`,
        fontFamily: kaiFontFamily,
        ...style,
        ...resolveStyleOverride(scrubberCss),
      }}
      {...props}
    >
      <div
        aria-hidden='true'
        style={{
          position: 'absolute',
          top: '50%',
          left: 0,
          right: 0,
          height: compact ? 4 : 6,
          transform: 'translateY(-50%)',
          borderRadius: compact ? REVIEW_RADII.circle : REVIEW_RADII.pill,
          background: REVIEW_COLORS.surfaceEmphasis,
        }}
      />
      <div
        aria-hidden='true'
        style={{
          position: 'absolute',
          top: '50%',
          left: 0,
          width: formatReviewFlowPercent(activePercent),
          height: compact ? 4 : 6,
          transform: 'translateY(-50%)',
          borderRadius: compact ? REVIEW_RADII.circle : REVIEW_RADII.pill,
          background: REVIEW_COLORS.borderStrong,
          transition: SCRUBBER_TRACK_TRANSITION,
        }}
      />
      <div
        data-review-flow-scrubber-thumb='true'
        aria-hidden='true'
        style={{
          position: 'absolute',
          top: '50%',
          left: formatReviewFlowPercent(activePercent),
          width: 14,
          height: 14,
          transform: activePointTransform,
          border: `2px solid ${thumbHighlighted ? SCRUBBER_HIGHLIGHT_BORDER : REVIEW_COLORS.text}`,
          borderRadius: compact ? REVIEW_RADII.circle : REVIEW_RADII.pill,
          background: REVIEW_COLORS.surface,
          boxShadow: thumbHighlighted ? SCRUBBER_HIGHLIGHT_SHADOW : '0 0 0 2px rgba(255, 255, 255, 0.9)',
          cursor: draggableCursor,
          transition: SCRUBBER_MOTION_TRANSITION,
        }}
      />
      {marks.map(({ key, item, placeholder, percent, distance }) => {
        if (placeholder || !item) {
          const opacity = Math.max(0.16, 0.42 - Math.abs(distance) * 0.06);
          return (
            <span
              key={key}
              aria-hidden='true'
              data-review-flow-scrubber-placeholder='true'
              data-review-flow-scrubber-distance={distance}
              style={{
                position: 'absolute',
                top: '50%',
                left: formatReviewFlowPercent(percent),
                width: 8,
                height: 8,
                transform: 'translate(-50%, -50%)',
                border: `1px dashed ${REVIEW_COLORS.borderStrong}`,
                borderRadius: compact ? REVIEW_RADII.circle : REVIEW_RADII.pill,
                background: 'transparent',
                opacity: compact ? 0 : opacity,
                transition: SCRUBBER_MOTION_TRANSITION,
              }}
            />
          );
        }

        const active = distance === 0;
        const disabled = model.disabled || item.disabled;
        const hovered = hoveredItemId === item.id && !disabled && !active;
        const snapHighlighted = snapHighlightItemId === item.id;
        const highlighted = hovered || snapHighlighted;
        const opacity = disabled ? 0.48 : Math.max(0.44, 1 - Math.abs(distance) * 0.08);
        const size = active ? 18 : Math.max(8, 12 - Math.max(Math.abs(distance) - 2, 0));
        const transform = 'translate(-50%, -50%)';

        return (
          <button
            key={item.id}
            type='button'
            aria-current={active ? 'true' : undefined}
            aria-label={`Go to ${item.label}`}
            data-review-flow-scrubber-item='true'
            data-review-flow-active={active ? 'true' : 'false'}
            data-review-flow-hovered={hovered ? 'true' : 'false'}
            data-review-flow-snap-highlight={snapHighlighted ? 'true' : 'false'}
            data-review-flow-item-id={item.id}
            data-review-flow-scrubber-distance={distance}
            disabled={disabled}
            onPointerEnter={disabled || active ? undefined : () => setHoveredItemId(item.id)}
            onPointerLeave={() => setHoveredItemId(currentItemId => currentItemId === item.id ? null : currentItemId)}
            onPointerDown={active ? undefined : event => event.stopPropagation()}
            onClick={() => onSelectItem?.(item.id)}
            style={{
              position: 'absolute',
              top: '50%',
              left: formatReviewFlowPercent(percent),
              width: compact ? (active ? 16 : 12) : size,
              height: compact ? 20 : size,
              transform,
              border: compact ? 0 : `1px solid ${highlighted ? SCRUBBER_HIGHLIGHT_BORDER : active ? REVIEW_COLORS.text : REVIEW_COLORS.borderStrong}`,
              borderRadius: compact ? REVIEW_RADII.circle : REVIEW_RADII.pill,
              background: compact
                ? 'transparent'
                : active
                ? REVIEW_COLORS.surface
                : highlighted
                ? SCRUBBER_HIGHLIGHT_BACKGROUND
                : REVIEW_COLORS.surfaceEmphasis,
              boxShadow: compact ? undefined : highlighted ? SCRUBBER_HIGHLIGHT_SHADOW : undefined,
              cursor: disabled ? 'not-allowed' : active ? draggableCursor : 'pointer',
              fontFamily: 'inherit',
              opacity: compact ? 0 : opacity,
              padding: 0,
              transition: SCRUBBER_MOTION_TRANSITION,
            }}
          />
        );
      })}
    </div>
  );
};
